// HeroArtwork v7 · eager + lazy hybrid.
//
// The 4 home-page artworks (institutional, bonus, markets, regulated)
// are statically imported so they paint INSTANTLY with the rest of the
// hero text · no Suspense flicker on first paint. The previous fully-
// lazy build caused a visible "freezes-left-then-fixes-itself" lag
// because the artwork chunk arrived ~hundreds of ms after the text
// column had already rendered from the main bundle.
//
// Tier/Regulated-page-specific variants stay lazy because they're
// only mounted when the user navigates to those inner pages.
//
// Variants (data-testid contracts unchanged so existing tests pass):
//   • institutional · 3-card 3D dashboard stack          · EAGER
//   • bonus         · luxury black-and-gold member card  · EAGER
//   • markets       · 6-tile asset-class grid            · EAGER
//   • regulated     · shield emblem + compliance certs   · EAGER
//   • islamic       · swap-free hero with phone mockup   · lazy
//   • social        · copy-trading hero                  · lazy
//   • mt5-trio      · MT5 desktop + tablet + phone trio  · lazy
//   • tier / classic / premier / elite                   · lazy
import React, { lazy, Suspense } from "react";

// Home-page artworks · eager-loaded so the hero paints in one frame.
import InstitutionalArt from "./heroArt/InstitutionalArt";
import BonusArt         from "./heroArt/BonusArt";
import MarketsArt       from "./heroArt/MarketsArt";
import RegulatedArt     from "./heroArt/RegulatedArt";

// Inner-page artworks · lazy-loaded so they don't bloat the main bundle.
const IslamicArt       = lazy(() => import(/* webpackChunkName: "art-islamic"       */ "./heroArt/IslamicArt"));
const SocialArt        = lazy(() => import(/* webpackChunkName: "art-social"        */ "./heroArt/SocialArt"));
const TierArt          = lazy(() => import(/* webpackChunkName: "art-tier"          */ "./heroArt/TierArt"));
const MT5TrioArt       = lazy(() => import(/* webpackChunkName: "art-mt5-trio"      */ "./heroArt/MT5TrioArt"));

// Re-export Sparkline as a static (non-lazy) module so any downstream
// imports of `{ Sparkline } from "@/components/HeroArtwork"` keep
// working without Suspense boundaries.
export { Sparkline } from "./heroArt/_shared";

/* Suspense fallback · transparent placeholder sized to the artwork
   column. The hero text on the left renders immediately; the artwork
   simply pops in a frame later (typically <100ms once the chunk lands
   in cache). No spinner, no skeleton — the empty space is intentional
   so users don't see a flicker of "loading…" UI on a hero. */
const Fallback = () => <div aria-hidden style={{ minHeight: "60vh" }} />;

function pick(variant, c, tier) {
  switch (variant) {
    case "institutional": return <InstitutionalArt accent={c} />;
    case "bonus":         return <BonusArt          accent={c} />;
    case "markets":       return <MarketsArt        accent={c} />;
    case "regulated":     return <RegulatedArt      accent={c} />;
    case "islamic":       return <IslamicArt        accent={c} />;
    case "social":        return <SocialArt         accent={c} />;
    case "mt5-trio":      return <MT5TrioArt        accent={c} />;
    case "classic":
    case "premier":
    case "elite":
    case "cent":
    case "tier":          return <TierArt           accent={c} tier={tier || variant} />;
    default:              return null;
  }
}

export default function HeroArtwork({ variant, accent, tier }) {
  const c = accent || "var(--brand)";
  const art = pick(variant, c, tier);
  if (!art) return null;
  // The 4 eager-loaded variants paint synchronously · no Suspense needed.
  // Lazy variants (islamic/social/tier/mt5-trio) still get the fallback.
  const isEager = variant === "institutional" || variant === "bonus" || variant === "markets" || variant === "regulated";
  return isEager ? art : <Suspense fallback={<Fallback />}>{art}</Suspense>;
}
